import { askAboutAndReveal } from "../../../state/chatActions";
import type { MerchantSummary, SiteAudit } from "../../../data/types";
import styles from "./SiteAuditCard.module.css";

interface SiteAuditCardProps {
  merchant: MerchantSummary;
  audit: SiteAudit;
}

const SUB_LABELS: { key: keyof SiteAudit["subscores"]; label: string }[] = [
  { key: "tcClarity", label: "t&c clarity" },
  { key: "refundPolicy", label: "refund policy" },
  { key: "cancellationEase", label: "cancellation ease" },
  { key: "productClarity", label: "product clarity" },
  { key: "mccAlignment", label: "mcc alignment" },
];

function tone(score: number): string {
  if (score >= 75) return "high";
  if (score >= 50) return "mid";
  return "low";
}

export function SiteAuditCard({ merchant, audit }: SiteAuditCardProps) {
  const overallTone = tone(audit.overall);
  const delta = audit.overall - audit.mccMedianOverall;
  return (
    <div className={styles.card}>
      <header className={styles.header}>
        <span className={styles.title}>site audit</span>
        <span className={styles.muted}>
          last audited {audit.auditedAt} · benchmark vs mcc {merchant.mcc}
        </span>
      </header>

      <div className={styles.headline}>
        <button
          type="button"
          className={`${styles.scoreBlock} ${styles[overallTone]}`}
          onClick={() =>
            askAboutAndReveal(
              `the site audit for ${merchant.name} shows an overall score of ${audit.overall}. walk me through what that score rolls up — which subscores are low, what flags were raised, and what we'd need the merchant to fix?`
            )
          }
        >
          <span className={styles.scoreLabel}>overall</span>
          <span className={styles.scoreValue}>{audit.overall}</span>
        </button>
        <div className={styles.benchBlock}>
          <span className={styles.scoreLabel}>mcc median</span>
          <span className={styles.benchValue}>{audit.mccMedianOverall}</span>
          <span
            className={`${styles.delta} ${
              delta < -5 ? styles.deltaBad : delta > 5 ? styles.deltaGood : styles.deltaFlat
            }`}
          >
            {delta > 0 ? "+" : ""}
            {delta}
          </span>
        </div>
      </div>

      <ul className={styles.subscores}>
        {SUB_LABELS.map(({ key, label }) => {
          const v = audit.subscores[key];
          const t = tone(v);
          return (
            <li key={key}>
              <button
                type="button"
                className={styles.subRow}
                onClick={() =>
                  askAboutAndReveal(
                    `${merchant.name}'s ${label} subscore is ${v}/100. what did the audit actually catch on this, and what does a strong version of this look like for mcc ${merchant.mcc}?`
                  )
                }
              >
                <span className={styles.subLabel}>{label}</span>
                <span className={styles.barWrap} aria-hidden>
                  <span
                    className={`${styles.barFill} ${styles[t]}`}
                    style={{ width: `${v}%` }}
                  />
                </span>
                <span className={`${styles.subValue} ${styles[t]}`}>{v}</span>
              </button>
            </li>
          );
        })}
      </ul>

      {audit.flags.length > 0 && (
        <div className={styles.flags}>
          <span className={styles.flagsLabel}>flags</span>
          <div className={styles.flagsList}>
            {audit.flags.map((f, i) => (
              <button
                key={i}
                type="button"
                className={styles.flagChip}
                onClick={() =>
                  askAboutAndReveal(
                    `${merchant.name} has a site-audit flag: "${f}". how would you resolve this with the merchant, and what's the fraud risk if they don't?`
                  )
                }
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
